import { Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from '@/components/AppShell'
import { Page } from '@/components/Page'
import { LoginPage } from '@/features/auth/LoginPage'
import { useAuth } from '@/features/auth/useAuth'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { AddPurchasePage } from '@/features/inventory/AddPurchasePage'
import { DeviceDetailPage } from '@/features/inventory/DeviceDetailPage'
import { DeviceEditPage } from '@/features/inventory/DeviceEditPage'
import { StockListPage } from '@/features/inventory/StockListPage'
import { SellPage } from '@/features/sales/SellPage'
import { ReceivablesPage } from '@/features/payments/ReceivablesPage'
import { TransactionsPage } from '@/features/transactions/TransactionsPage'
import { ContactsPage } from '@/features/contacts/ContactsPage'
import { ExpensesPage } from '@/features/expenses/ExpensesPage'
import { ReportsPage } from '@/features/reports/ReportsPage'
import { SyncPage } from '@/features/sync/SyncPage'
import { CategoriesPage } from '@/features/categories/CategoriesPage'
import { BackupPage } from '@/features/backup/BackupPage'
import { RequireWorkspace } from '@/features/workspace/RequireWorkspace'

export default function App() {
  const { configured, loading, session } = useAuth()

  if (!configured) return <ConfigNotice />
  if (loading) return <Splash>Loading…</Splash>
  if (!session) return <LoginPage />

  return (
    <RequireWorkspace>
      <AppShell>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/setup" element={<Page title="Setup" note="Recovery secret + backup settings — Phase 5." />} />

          <Route path="/inventory" element={<StockListPage />} />
          <Route path="/inventory/new" element={<AddPurchasePage />} />
          <Route path="/inventory/:deviceId" element={<DeviceDetailPage />} />
          <Route path="/inventory/:deviceId/edit" element={<DeviceEditPage />} />
          <Route path="/inventory/:deviceId/sell" element={<SellPage />} />

          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/receivables" element={<ReceivablesPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/reports" element={<ReportsPage />} />

          <Route path="/settings/categories" element={<CategoriesPage />} />
          <Route path="/settings/backup" element={<BackupPage />} />
          <Route path="/sync" element={<SyncPage />} />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AppShell>
    </RequireWorkspace>
  )
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-950 text-sm text-slate-300">
      {children}
    </div>
  )
}

function ConfigNotice() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-950 px-6 text-center text-sm text-slate-300">
      <p>
        Supabase is not configured. Set <code>VITE_SUPABASE_URL</code> and{' '}
        <code>VITE_SUPABASE_PUBLISHABLE_KEY</code> in <code>.env</code> (see <code>.env.example</code>),
        then rebuild.
      </p>
    </div>
  )
}
